import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Alert,
  CircularProgress,
  IconButton,
  Stack,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import AlertItem from '../../Components/AlertItem';
import {
  ArrowBack as ArrowBackIcon,
  PlayArrow as PlayArrowIcon,
  Close as CloseIcon,
  ContentCopy as ContentCopyIcon,
  Assessment as AssessmentIcon,
  PictureAsPdf as PictureAsPdfIcon,
} from '@mui/icons-material';
import { useNavigate, useParams, Link as RouterLink } from 'react-router-dom';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LineChart } from '@mui/x-charts/LineChart';
import dayjs, { Dayjs } from 'dayjs';
import { useApiGet } from '../../utils/httpClientTs';
import {
  getPEOReportMetadata,
  executePEOReport,
  exportPEOReportEmails,
  downloadPEOReportPDF,
} from '../../endpoints/peoReportsTs';
import {
  ReportMetadata,
  ExecuteReportRequest,
  ExecuteReportResponse,
  REPORT_ID_ACTIVE_INACTIVE_CHAPTER_MEMBERS,
  REPORT_ID_MEMBERSHIP_YEAR_FINAL,
  REPORT_ID_CHAPTER_TRANSACTION_LOG,
  REPORT_ID_CHAPTER_MEMBERSHIP_HISTORY,
  REPORT_ID_CHAPTER_LISTING,
  REPORT_ID_CHAPTER_HEALTH,
  REPORT_ID_INCREASE_DECREASE_BY_CHAPTER,
  REPORT_ID_CHAPTER_STAR,
  REPORT_ID_MEMBERSHIP_YOY,
  REPORT_ID_INTERNATIONAL_REP_OFFICERS_CONFERENCE,
  REPORT_ID_MEMBERSHIP_GRAPHS,
  REPORT_ID_ANNUAL_REPORT_OF_MEMBERSHIP,
  REPORT_ID_LOCAL_CHAPTER_OFFICER_ELECTION,
} from '../../types/peoReports';
import ClientSideTable, {
  ClientSideTableColumn,
} from '../../Components/ClientSideTable';
import ExportButton from '../../Components/ExportButton';
import ChapterSelector from '../../Components/ChapterSelector';
import SPDSelector from '../../Components/SPDSelector';
import MemberActiveFilter from '../../Components/MemberActiveFilter';
import LocalChapterRolesFilter from '../../Components/LocalChapterRolesFilter';
import YearsAgoFilter from '../../Components/YearsAgoFilter';

const PEOReportDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [bannerDismissed, setBannerDismissed] = useState(() => {
    return localStorage.getItem('peo-reports-banner-dismissed') === 'true';
  });

  const [filterValues, setFilterValues] = useState<ExecuteReportRequest>({});
  const [reportData, setReportData] = useState<ExecuteReportResponse | null>(
    null
  );
  const [isExecuting, setIsExecuting] = useState(false);
  const [isPdfDownloading, setIsPdfDownloading] = useState(false);
  const [executionError, setExecutionError] = useState<string | null>(null);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailData, setEmailData] = useState<string>('');
  const [emailCount, setEmailCount] = useState<number>(0);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<
    Record<string, boolean>
  >({});

  const {
    data: metadata,
    isLoading,
    error,
  } = useApiGet<ReportMetadata>(
    ['peo-report-metadata', id],
    () => getPEOReportMetadata(id!),
    {
      enabled: !!id,
      retry: 1,
    }
  );

  const hasEmailColumn =
    metadata?.columns?.some((col) => col.type === 'email') || false;

  // Initialize column visibility from metadata
  useEffect(() => {
    if (metadata?.columns) {
      const initialVisibility: Record<string, boolean> = {};
      metadata.columns.forEach((col) => {
        initialVisibility[col.field] = col.visible;
      });
      setColumnVisibilityModel(initialVisibility);
    }
  }, [metadata]);

  // Current membership year (March 1–March 1): year of the start date
  const getCurrentMembershipYear = () => {
    const now = new Date();
    return now.getMonth() >= 2 ? now.getFullYear() : now.getFullYear() - 1;
  };

  // Initialize filter values from parameter defaults (only when current value is undefined)
  useEffect(() => {
    if (!metadata?.parameters?.length) return;
    setFilterValues((prev) => {
      let next = prev;
      for (const param of metadata.parameters) {
        if (param.default_value == null || param.default_value === '') continue;
        const key = param.name as keyof ExecuteReportRequest;
        const current = prev[key];
        if (current !== undefined && current !== '') continue;
        const value =
          param.type === 'number'
            ? Number(param.default_value)
            : param.default_value;
        if (next === prev) next = { ...prev };
        (next as Record<string, unknown>)[param.name] = value;
      }
      return next;
    });
  }, [metadata]);

  // Default membership_year for Membership Year Final report when undefined
  useEffect(() => {
    if (!id || id !== REPORT_ID_MEMBERSHIP_YEAR_FINAL || !metadata) return;
    setFilterValues((prev) => {
      if (prev.membership_year != null) return prev;
      return { ...prev, membership_year: getCurrentMembershipYear() };
    });
  }, [id, metadata]);

  // Default membership_year for Annual Report of Membership when undefined
  useEffect(() => {
    if (!id || id !== REPORT_ID_ANNUAL_REPORT_OF_MEMBERSHIP || !metadata)
      return;
    setFilterValues((prev) => {
      if (prev.membership_year != null) return prev;
      return { ...prev, membership_year: getCurrentMembershipYear() };
    });
  }, [id, metadata]);

  const handleDismissBanner = () => {
    localStorage.setItem('peo-reports-banner-dismissed', 'true');
    setBannerDismissed(true);
  };

  const handleFilterChange = (field: string, value: any) => {
    setFilterValues((prev) => ({ ...prev, [field]: value }));
  };

  const isRequiredFieldMissing = () => {
    if (!metadata) return true;
    return metadata.parameters.some((param) => {
      if (!param.required) return false;
      const value = filterValues[param.name as keyof ExecuteReportRequest];
      return !value || value === '';
    });
  };

  const handleExecuteReport = async () => {
    if (!id) return;

    setIsExecuting(true);
    setExecutionError(null);

    try {
      const params =
        id === REPORT_ID_ACTIVE_INACTIVE_CHAPTER_MEMBERS &&
        filterValues.years_ago_inactive === undefined
          ? { ...filterValues, years_ago_inactive: 10 }
          : filterValues;
      const response = await executePEOReport(id, {
        ...params,
        format: 'json',
      });
      setReportData(response);
    } catch (err: any) {
      console.error('Report execution failed:', err);
      setExecutionError(
        err.message || 'Failed to execute report. Please try again.'
      );
    } finally {
      setIsExecuting(false);
    }
  };

  const handleEmailExport = async () => {
    if (!id) return;

    try {
      const params =
        id === REPORT_ID_ACTIVE_INACTIVE_CHAPTER_MEMBERS &&
        filterValues.years_ago_inactive === undefined
          ? { ...filterValues, years_ago_inactive: 10 }
          : filterValues;
      const response = await exportPEOReportEmails(id, params);
      setEmailData(response.emails);
      setEmailCount(response.count);
      setEmailDialogOpen(true);
    } catch (err: any) {
      console.error('Email export failed:', err);
      setSnackbarMessage('Failed to export emails. Please try again.');
      setSnackbarOpen(true);
    }
  };

  const handleDownloadPDF = async () => {
    if (!id) return;

    setIsPdfDownloading(true);
    try {
      const params =
        id === REPORT_ID_ACTIVE_INACTIVE_CHAPTER_MEMBERS &&
        filterValues.years_ago_inactive === undefined
          ? { ...filterValues, years_ago_inactive: 10 }
          : filterValues;
      const blob = await downloadPEOReportPDF(id, params);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download =
        id === REPORT_ID_MEMBERSHIP_YEAR_FINAL
          ? 'membership-year-final.pdf'
          : id === REPORT_ID_CHAPTER_TRANSACTION_LOG
            ? 'chapter-transaction-log.pdf'
            : id === REPORT_ID_CHAPTER_MEMBERSHIP_HISTORY
              ? 'chapter-membership-history.pdf'
              : id === REPORT_ID_CHAPTER_LISTING
                ? 'chapter-listing.pdf'
                : id === REPORT_ID_CHAPTER_HEALTH
                  ? 'chapter-health.pdf'
                  : id === REPORT_ID_INCREASE_DECREASE_BY_CHAPTER
                    ? 'increase-decrease-by-chapter.pdf'
                    : id === REPORT_ID_CHAPTER_STAR
                      ? 'chapter-star-report.pdf'
                      : id === REPORT_ID_MEMBERSHIP_YOY
                        ? 'membership-yoy.pdf'
                        : id === REPORT_ID_INTERNATIONAL_REP_OFFICERS_CONFERENCE
                          ? 'international-rep-officers-conference.pdf'
                          : id === REPORT_ID_MEMBERSHIP_GRAPHS
                            ? 'membership-graphs.pdf'
                            : id === REPORT_ID_ANNUAL_REPORT_OF_MEMBERSHIP
                              ? 'annual-report-of-membership.pdf'
                              : 'active-inactive-chapter-members.pdf';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setSnackbarMessage('PDF downloaded.');
      setSnackbarOpen(true);
    } catch (err: any) {
      console.error('PDF download failed:', err);
      setSnackbarMessage(
        err.message || 'Failed to download PDF. Please try again.'
      );
      setSnackbarOpen(true);
    } finally {
      setIsPdfDownloading(false);
    }
  };

  const handleCopyEmails = () => {
    navigator.clipboard.writeText(emailData);
    setSnackbarMessage('Email addresses copied to clipboard!');
    setSnackbarOpen(true);
  };

  const formatDateForAPI = (date: Dayjs | null): string => {
    if (!date) return '';
    return date.format('YYYY-MM-DD');
  };

  // For Local Chapter Officer Election report, show only columns matching report_mode (lc vs spd)
  const effectiveColumns =
    id === REPORT_ID_LOCAL_CHAPTER_OFFICER_ELECTION && reportData?.report_mode
      ? (metadata?.columns?.filter(
          (col) => !col.mode || col.mode === reportData.report_mode
        ) ?? [])
      : (metadata?.columns ?? []);

  // Map report columns to ClientSideTable format
  const tableColumns: ClientSideTableColumn<any>[] = effectiveColumns.map(
    (col) => ({
      id: col.field,
      label: col.label,
      width: `${col.width}px`,
      sortable: col.sortable,
    })
  );

  // Prepare export columns (only include visible columns)
  const exportColumns = effectiveColumns
    .filter((col) => columnVisibilityModel[col.field] !== false)
    .map((col) => ({
      field: col.field,
      headerName: col.label,
      width: col.width,
    }));

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error || !metadata) {
    return (
      <Box sx={{ p: 3 }}>
        <AlertItem
          severity="error"
          description={
            error
              ? 'Failed to load report metadata. Please try again.'
              : 'Report not found.'
          }
          onClose={() => {}}
          keepOpen={true}
        />
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/peo-reports')}
        >
          Back to Reports
        </Button>
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ p: 3, pb: 8 }}>
        {/* Banner for old reports link */}
        {!bannerDismissed && (
          <Alert
            severity="info"
            icon={<AssessmentIcon />}
            action={
              <IconButton
                size="small"
                onClick={handleDismissBanner}
                aria-label="close"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            }
            sx={{ mb: 3 }}
          >
            Looking for the previous reports system?{' '}
            <RouterLink
              to="/reports"
              style={{
                color: 'inherit',
                fontWeight: 600,
                textDecoration: 'underline',
              }}
            >
              Use previous Hub Reports
            </RouterLink>
          </Alert>
        )}

        {/* Header */}
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
          <IconButton onClick={() => navigate('/peo-reports')}>
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
              {metadata.name}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {metadata.description}
            </Typography>
          </Box>
        </Stack>

        {/* Filters Card */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom fontWeight={600}>
              Report Parameters
            </Typography>
            <Divider sx={{ mb: 3 }} />

            {metadata.parameters.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                This report does not require any parameters.
              </Typography>
            ) : (
              <Grid container spacing={3}>
                {metadata.parameters.map((param) => {
                  const isRequired = param.required;
                  const label = isRequired
                    ? `${param.description} *`
                    : param.description;

                  if (param.name === 'local_chapter_id') {
                    return (
                      <Grid item xs={12} md={6} key={param.name}>
                        <ChapterSelector
                          chapter={filterValues.local_chapter_id || ''}
                          spd={filterValues.spd_chapter_id || null}
                          keyId={id!}
                          handleChapterSelect={(value: string | any | null) =>
                            handleFilterChange('local_chapter_id', value || '')
                          }
                        />
                      </Grid>
                    );
                  }

                  if (param.name === 'spd_chapter_id') {
                    return (
                      <Grid item xs={12} md={6} key={param.name}>
                        <SPDSelector
                          spd={filterValues.spd_chapter_id || ''}
                          keyId={id!}
                          reportId={id!}
                          handleSpdSelect={(value: string | any | null) =>
                            handleFilterChange('spd_chapter_id', value || '')
                          }
                        />
                      </Grid>
                    );
                  }

                  if (param.name === 'status') {
                    return (
                      <Grid item xs={12} sm={6} md={4} key={param.name}>
                        <MemberActiveFilter
                          filterValue={filterValues.status || ''}
                          updateFilters={(value: string) =>
                            handleFilterChange('status', value)
                          }
                          disabled={false}
                        />
                      </Grid>
                    );
                  }

                  if (param.name === 'local_chapter_roles') {
                    return (
                      <Grid item xs={12} md={6} key={param.name}>
                        <LocalChapterRolesFilter
                          value={filterValues.local_chapter_roles || ''}
                          onChange={(value: string) =>
                            handleFilterChange('local_chapter_roles', value)
                          }
                          disabled={false}
                          required={param.required}
                        />
                      </Grid>
                    );
                  }

                  if (param.name === 'only_disbanded') {
                    const onlyDisbandedLabel = isRequired
                      ? 'Only Disbanded? *'
                      : 'Only Disbanded?';
                    return (
                      <Grid item xs={12} sm={6} md={4} key={param.name}>
                        <FormControl
                          fullWidth
                          size="small"
                          required={isRequired}
                        >
                          <InputLabel id="only-disbanded-label">
                            {onlyDisbandedLabel}
                          </InputLabel>
                          <Select
                            labelId="only-disbanded-label"
                            id="only-disbanded-select"
                            value={
                              filterValues.only_disbanded === undefined
                                ? ''
                                : filterValues.only_disbanded === true
                                  ? 'true'
                                  : 'false'
                            }
                            label={onlyDisbandedLabel}
                            onChange={(e) => {
                              const value = e.target.value;
                              handleFilterChange(
                                'only_disbanded',
                                value === '' ? undefined : value === 'true'
                              );
                            }}
                          >
                            <MenuItem value="true">Yes</MenuItem>
                            <MenuItem value="false">No</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                    );
                  }

                  if (param.name === 'years_ago_inactive') {
                    const yearsAgoLabel = isRequired
                      ? 'Inactive within (years) *'
                      : 'Inactive within (years)';
                    return (
                      <Grid item xs={12} sm={6} md={4} key={param.name}>
                        <YearsAgoFilter
                          value={filterValues.years_ago_inactive}
                          onChange={(v) =>
                            handleFilterChange('years_ago_inactive', v)
                          }
                          label={yearsAgoLabel}
                          required={isRequired}
                          allowNoLimit={true}
                        />
                      </Grid>
                    );
                  }

                  if (param.name === 'membership_year') {
                    const membershipYearLabel = isRequired
                      ? 'Membership Year *'
                      : 'Membership Year';
                    // Membership year starts March 1; max = current MY, min = 2010
                    const maxYear = getCurrentMembershipYear();
                    const minYear = 2010;
                    const years = Array.from(
                      { length: maxYear - minYear + 1 },
                      (_, i) => maxYear - i
                    );
                    const isIROC =
                      id === REPORT_ID_INTERNATIONAL_REP_OFFICERS_CONFERENCE;

                    return (
                      <Grid item xs={12} sm={6} md={4} key={param.name}>
                        <FormControl
                          fullWidth
                          size="small"
                          required={isRequired}
                        >
                          <InputLabel id="membership-year-label">
                            {membershipYearLabel}
                          </InputLabel>
                          <Select
                            labelId="membership-year-label"
                            id="membership-year-select"
                            value={
                              filterValues.membership_year === undefined
                                ? ''
                                : String(filterValues.membership_year)
                            }
                            label={membershipYearLabel}
                            onChange={(e) => {
                              const value = e.target.value;
                              handleFilterChange(
                                'membership_year',
                                value === '' ? undefined : Number(value)
                              );
                            }}
                          >
                            {isIROC && (
                              <MenuItem value="">
                                Current (as of today)
                              </MenuItem>
                            )}
                            {years.map((year) => {
                              const nextYear = (year + 1).toString().slice(-2); // Get last 2 digits
                              return (
                                <MenuItem key={year} value={String(year)}>
                                  {year}-{nextYear}
                                </MenuItem>
                              );
                            })}
                          </Select>
                        </FormControl>
                      </Grid>
                    );
                  }

                  if (
                    param.type === 'date' &&
                    (param.name === 'as_of_date' ||
                      param.name === 'start_date' ||
                      param.name === 'end_date')
                  ) {
                    // Use shorter labels for date fields
                    let dateLabel = label;
                    if (param.name === 'start_date') {
                      dateLabel = isRequired ? 'Start Date *' : 'Start Date';
                    } else if (param.name === 'end_date') {
                      dateLabel = isRequired ? 'End Date *' : 'End Date';
                    }
                    // as_of_date keeps the original label from param.description

                    return (
                      <Grid item xs={12} md={6} key={param.name}>
                        <DatePicker
                          label={dateLabel}
                          value={
                            filterValues[param.name]
                              ? dayjs(filterValues[param.name])
                              : null
                          }
                          onChange={(newValue) =>
                            handleFilterChange(
                              param.name,
                              formatDateForAPI(newValue)
                            )
                          }
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              size: 'small',
                              required: isRequired,
                            },
                          }}
                        />
                      </Grid>
                    );
                  }

                  return null;
                })}
              </Grid>
            )}

            <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
              {id !== REPORT_ID_INTERNATIONAL_REP_OFFICERS_CONFERENCE &&
                id !== REPORT_ID_MEMBERSHIP_GRAPHS &&
                id !== REPORT_ID_ANNUAL_REPORT_OF_MEMBERSHIP && (
                  <Button
                    variant="contained"
                    startIcon={<PlayArrowIcon />}
                    onClick={handleExecuteReport}
                    disabled={isExecuting || isRequiredFieldMissing()}
                  >
                    {isExecuting ? 'Running Report...' : 'Run Report'}
                  </Button>
                )}
              {(id === REPORT_ID_INTERNATIONAL_REP_OFFICERS_CONFERENCE ||
                id === REPORT_ID_MEMBERSHIP_GRAPHS ||
                id === REPORT_ID_ANNUAL_REPORT_OF_MEMBERSHIP ||
                id === REPORT_ID_ACTIVE_INACTIVE_CHAPTER_MEMBERS ||
                id === REPORT_ID_MEMBERSHIP_YEAR_FINAL ||
                id === REPORT_ID_CHAPTER_TRANSACTION_LOG ||
                id === REPORT_ID_CHAPTER_MEMBERSHIP_HISTORY ||
                id === REPORT_ID_CHAPTER_LISTING ||
                id === REPORT_ID_CHAPTER_HEALTH ||
                id === REPORT_ID_INCREASE_DECREASE_BY_CHAPTER ||
                id === REPORT_ID_CHAPTER_STAR) && (
                <Button
                  variant={
                    id === REPORT_ID_INTERNATIONAL_REP_OFFICERS_CONFERENCE ||
                    id === REPORT_ID_MEMBERSHIP_GRAPHS ||
                    id === REPORT_ID_ANNUAL_REPORT_OF_MEMBERSHIP
                      ? 'contained'
                      : 'outlined'
                  }
                  startIcon={<PictureAsPdfIcon />}
                  onClick={handleDownloadPDF}
                  disabled={isPdfDownloading || isRequiredFieldMissing()}
                >
                  {isPdfDownloading ? 'Downloading PDF...' : 'Download PDF'}
                </Button>
              )}
            </Box>
          </CardContent>
        </Card>

        {/* Execution Error */}
        {executionError && (
          <Box sx={{ mb: 3 }}>
            <AlertItem
              severity="error"
              description={executionError}
              onClose={() => setExecutionError(null)}
              keepOpen={true}
            />
          </Box>
        )}

        {/* Results Card */}
        {reportData && (
          <Card>
            <CardContent>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 2 }}
              >
                <Box>
                  <Typography variant="h6" fontWeight={600}>
                    Report Results
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {reportData.row_count} record
                    {reportData.row_count !== 1 ? 's' : ''} • Executed in{' '}
                    {reportData.execution_time}ms
                  </Typography>
                </Box>
                <ExportButton
                  data={reportData.data || []}
                  columns={exportColumns}
                  filename={`${metadata.name.replace(/\s+/g, '_')}_${dayjs().format('YYYY-MM-DD')}`}
                  hasEmailColumn={hasEmailColumn}
                  onEmailExport={handleEmailExport}
                  disableTableExport={
                    id === REPORT_ID_CHAPTER_STAR ||
                    id === REPORT_ID_INTERNATIONAL_REP_OFFICERS_CONFERENCE ||
                    id === REPORT_ID_MEMBERSHIP_GRAPHS ||
                    id === REPORT_ID_ANNUAL_REPORT_OF_MEMBERSHIP
                  }
                  hasPdfExport={
                    id === REPORT_ID_ACTIVE_INACTIVE_CHAPTER_MEMBERS ||
                    id === REPORT_ID_MEMBERSHIP_YEAR_FINAL ||
                    id === REPORT_ID_CHAPTER_TRANSACTION_LOG ||
                    id === REPORT_ID_CHAPTER_MEMBERSHIP_HISTORY ||
                    id === REPORT_ID_CHAPTER_LISTING ||
                    id === REPORT_ID_CHAPTER_HEALTH ||
                    id === REPORT_ID_INCREASE_DECREASE_BY_CHAPTER ||
                    id === REPORT_ID_CHAPTER_STAR ||
                    id === REPORT_ID_INTERNATIONAL_REP_OFFICERS_CONFERENCE ||
                    id === REPORT_ID_MEMBERSHIP_GRAPHS ||
                    id === REPORT_ID_ANNUAL_REPORT_OF_MEMBERSHIP
                  }
                  onPdfExport={handleDownloadPDF}
                  isPdfDownloading={isPdfDownloading}
                />
              </Stack>

              <Divider sx={{ my: 2 }} />

              {!reportData.data || reportData.data.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    No data found for the selected parameters.
                  </Typography>
                </Box>
              ) : id === REPORT_ID_CHAPTER_STAR &&
                Array.isArray(reportData.data) &&
                reportData.data.length > 0 &&
                reportData.data.every(
                  (row: unknown) =>
                    row != null &&
                    typeof row === 'object' &&
                    'year' in row &&
                    'active_count' in row
                ) ? (
                <Box>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={{ mb: 2, textAlign: 'center' }}
                  >
                    Number of active members as of the end of each membership
                    year.
                  </Typography>
                  <LineChart
                    height={320}
                    series={[
                      {
                        data: (
                          reportData.data as {
                            year: number;
                            active_count: number;
                          }[]
                        ).map((d) => d.active_count),
                        label: 'Active count',
                      },
                    ]}
                    xAxis={[
                      {
                        scaleType: 'point',
                        data: (
                          reportData.data as {
                            year: number;
                            active_count: number;
                          }[]
                        ).map((d) => String(d.year)),
                      },
                    ]}
                    yAxis={[{ scaleType: 'linear', label: 'Active count' }]}
                    margin={{ top: 20, right: 30, bottom: 30, left: 50 }}
                  />
                </Box>
              ) : (
                <ClientSideTable
                  data={reportData.data}
                  columns={tableColumns}
                  enableColumnVisibility={true}
                  columnVisibilityModel={columnVisibilityModel}
                  onColumnVisibilityChange={setColumnVisibilityModel}
                  initialRowsPerPage={25}
                  rowsPerPageOptions={[10, 25, 50, 100, 250]}
                />
              )}
            </CardContent>
          </Card>
        )}

        {/* Email Export Dialog */}
        <Dialog
          open={emailDialogOpen}
          onClose={() => setEmailDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            Email List Export
            <IconButton
              onClick={() => setEmailDialogOpen(false)}
              sx={{ position: 'absolute', right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {emailCount} unique email address{emailCount !== 1 ? 'es' : ''}{' '}
              found. Emails are separated by semicolons for easy copy/paste into
              email clients.
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={10}
              value={emailData}
              InputProps={{
                readOnly: true,
              }}
              sx={{ mb: 2 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEmailDialogOpen(false)}>Close</Button>
            <Button
              variant="contained"
              startIcon={<ContentCopyIcon />}
              onClick={handleCopyEmails}
            >
              Copy to Clipboard
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={3000}
          onClose={() => setSnackbarOpen(false)}
          message={snackbarMessage}
        />
      </Box>
    </LocalizationProvider>
  );
};

export default PEOReportDetail;
